import type { ReactNode } from "react";

import { Box, Button, Callout, Flex, Text } from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import {
  DataTable,
  type DataTableColumns,
} from "~/v3-poc/components/DataTable";
import type {
  PositionsWidgetFilters,
  PositionsWidgetRow,
} from "./PositionsWidget.types";
import {
  positionsEmptyTableMessage,
  positionsOpenCountLabel,
} from "./PositionsWidget.utils";
import type { PositionsTableDensity } from "./usePositionsTableDisplay";
import "./PositionsWidget.table.css";

const NO_DETAIL_PANEL = (_row: PositionsWidgetRow): ReactNode => null;

export const PositionsOpenCountLabel = ({
  shown,
  total,
}: {
  shown: number;
  total: number;
}) => (
  <Text size="1" color="gray" className="shrink-0">
    {positionsOpenCountLabel(shown, total)}
  </Text>
);

export interface PositionsWidgetPresentationProps {
  surface?: "standalone" | "workspace";
  filters: PositionsWidgetFilters;
  rows: PositionsWidgetRow[];
  openCount?: number;
  columns: DataTableColumns<PositionsWidgetRow>;
  selectedIds?: readonly string[];
  validationError?: string;
  isAccountReady: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage?: string;
  density?: PositionsTableDensity;
  horizontalLines?: boolean;
  verticalLines?: boolean;
  detailRowId?: string | null;
  renderDetailPanel?: (row: PositionsWidgetRow) => ReactNode;
  onRetry: () => void;
  openCountSlot?: ReactNode;
  toolbarSlot: ReactNode;
  embeddedTicketSlot?: ReactNode;
  closeDialogSlot: ReactNode;
  rollDialogSlot: ReactNode;
  liquidateDialogSlot: ReactNode;
}

const getPositionsEmptyMessage = ({
  isAccountReady,
  isLoading,
  search,
}: {
  isAccountReady: boolean;
  isLoading: boolean;
  search: string;
}): string => {
  if (!isAccountReady) {
    return "Select an account to load positions.";
  }
  if (isLoading) {
    return "Loading positions…";
  }
  return search.trim()
    ? positionsEmptyTableMessage(search)
    : "No open positions";
};

const getPositionsTableHostClass = (
  density: PositionsTableDensity,
  horizontalLines: boolean,
  verticalLines: boolean,
): string =>
  cn(
    "positions-widget-table min-h-0 min-w-0",
    density === "condensed" && "positions-widget-table--condensed",
    density === "expanded" && "positions-widget-table--expanded",
    !horizontalLines && "positions-widget-table--no-hlines",
    verticalLines && "positions-widget-table--vlines",
  );

export const PositionsWidgetPresentation = ({
  surface = "standalone",
  filters,
  rows,
  openCount,
  columns,
  selectedIds = [],
  validationError,
  isAccountReady,
  isLoading,
  isError,
  errorMessage,
  density = "normal",
  horizontalLines = true,
  verticalLines = false,
  detailRowId = null,
  renderDetailPanel = NO_DETAIL_PANEL,
  onRetry,
  openCountSlot,
  toolbarSlot,
  embeddedTicketSlot,
  closeDialogSlot,
  rollDialogSlot,
  liquidateDialogSlot,
}: PositionsWidgetPresentationProps) => {
  const isWorkspace = surface === "workspace";
  const selectedCount = selectedIds.length;
  const tableHostClass = getPositionsTableHostClass(
    density,
    horizontalLines,
    verticalLines,
  );
  const emptyMessage = getPositionsEmptyMessage({
    isAccountReady,
    isLoading,
    search: filters.search,
  });

  return (
    <Flex
      direction="column"
      gap="1"
      height={isWorkspace ? "100%" : undefined}
      className={cn(isWorkspace && "min-h-0")}
      data-testid="positions-widget-content"
    >
      {isWorkspace ? (
        openCountSlot
      ) : openCount !== undefined ? (
        <PositionsOpenCountLabel shown={rows.length} total={openCount} />
      ) : null}
      {toolbarSlot}

      {validationError ? (
        <Box px={isWorkspace ? "2" : "0"}>
          <Callout.Root color="amber" size="1">
            <Callout.Text>{validationError}</Callout.Text>
          </Callout.Root>
        </Box>
      ) : null}

      {isError ? (
        <Box px={isWorkspace ? "2" : "0"}>
          <Callout.Root color="red" size="1">
            <Callout.Text>
              {errorMessage?.trim() || "Couldn’t load positions."}{" "}
              <Button type="button" size="1" variant="ghost" onClick={onRetry}>
                Retry
              </Button>
            </Callout.Text>
          </Callout.Root>
        </Box>
      ) : null}

      <Flex
        flexGrow="1"
        minHeight="0"
        minWidth="0"
        overflow="hidden"
        className={cn(isWorkspace && "min-h-0")}
      >
        <Box
          flexGrow="1"
          px={isWorkspace ? "1" : "0"}
          minWidth="0"
          minHeight="0"
          className={tableHostClass}
        >
          <div className="positions-widget-table-scroll">
            <DataTable
              columns={columns}
              data={rows}
              variant="ghost"
              size="1"
              caption="Positions"
              emptyMessage={emptyMessage}
              className={tableHostClass}
              getRowId={(row) => row.id}
              forceExpandedId={detailRowId}
              renderExpanded={renderDetailPanel}
              isRowSelected={
                selectedCount > 0
                  ? (row) => selectedIds.includes(row.id)
                  : undefined
              }
              clampExpandedToViewport
            />
          </div>
        </Box>
        {embeddedTicketSlot ? (
          <Box
            flexShrink="0"
            minHeight="0"
            minWidth="0"
            className="w-[min(22rem,45%)]"
          >
            {embeddedTicketSlot}
          </Box>
        ) : null}
      </Flex>

      {closeDialogSlot}
      {rollDialogSlot}
      {liquidateDialogSlot}
    </Flex>
  );
};
